import { useEffect, useMemo, useState } from 'react';

import {
  ArrowDown02Icon,
  Cancel01Icon,
  HelpCircleIcon,
  HonourStarIcon,
  ShieldKeyIcon,
  UserTime02Icon,
} from '@hugeicons/core-free-icons';
import { HugeiconsIcon } from '@hugeicons/react';
import cn from 'classnames';
import { CandidateHelpModalContent } from 'constants/candidate-form';
import DOMPurify from 'dompurify';
import { buildNavPath as buildNavPathHelper } from 'helpers/candidate_application/form-page-helpers';
import { getCheckTypeIcon } from 'helpers/iconsHelper';
import { useQueryParams } from 'hooks/query-params';
import { useScrollAreaOverflow } from 'hooks/use-scroll-area-overflow';
import CandidatePage from 'layouts/CandidatePage/CandidatePage';
import { AnimatePresence, motion } from 'motion/react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { useDispatch } from 'react-redux';
import { useNavigate, useParams } from 'react-router-dom';

import { CustomButton } from '../components/CustomButton/CustomButton';
import Alert from 'components/Alert/Alert';
import { AnimatedModal } from 'components/AnimatedModal/AnimatedModal';
import { Badge } from 'components/Badge/Badge';
import { IconComponent } from 'components/Icon/IconComponent';
import SliderMotion from 'components/Motion/SliderMotion';

import {
  cancelCandidateChecks,
  getCandidateApplication,
  selectCandidateApplication,
  updateCandidateCheck,
} from 'api/candidate-application-slice';
import { useSetHistoryLinkCandidateByTokenMutation } from 'api/candidates-api';

import styles from './CandidateIntroduction.module.scss';

const introCheckListVariants = {
  action: {
    sectionClass: 'px-8 pt-8 pb-4',
    badgeClass:
      'inline-flex h-8 w-10 shrink-0 items-center justify-center rounded-3xl bg-primary-4/20 text-center text-xl font-medium leading-none text-primary-4 tabular-nums',
  },
  done: {
    sectionClass: 'px-8 pb-8',
    badgeClass:
      'inline-flex h-8 w-10 shrink-0 items-center justify-center rounded-3xl bg-green-1 text-center text-xl font-medium leading-none text-green-3 tabular-nums',
  },
};

const IntroductionCheckListSection = ({ title, rows, variant = 'action' }) => {
  const list = rows || [];
  const { sectionClass, badgeClass } =
    introCheckListVariants[variant] || introCheckListVariants.action;
  if (!list.length) {
    return null;
  }
  return (
    <div className={cn('flex flex-col gap-4', sectionClass)}>
      <div className="flex flex-col gap-2">
        <div className="flex items-center gap-4">
          <span className="m-0 text-lg font-semibold text-neutral-7 text-uppercase">
            {title ?? ''}
          </span>
          <span className={badgeClass}>{list.length}</span>
        </div>
      </div>
      <div className="flex flex-col gap-4">
        {list.map((row, index) => {
          if (row == null) return null;
          return (
            <div
              key={row.key ?? `row-${index}`}
              className="flex min-w-0 items-center gap-4 rounded-2xl">
              <IconComponent
                icon={getCheckTypeIcon(row?.slug)}
                strokeWidth="2"
                size="medium"
                {...(variant === 'done' ? { type: 'successPin' } : {})}
              />
              <span className="min-w-0 flex-1 text-neutral-7 text-2xl font-regular leading-snug line-clamp-2 break-words">
                {row?.title ?? ''}
              </span>
            </div>
          );
        })}
      </div>
    </div>
  );
};

const CandidateIntroduction = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { token } = useParams();

  const { application: applicationFromStore } = useSelector(selectCandidateApplication) || {};
  const { candidate, brand, candidate_checks, check_types, company_name } =
    applicationFromStore || {};

  const [setHistoryLinkCandidate] = useSetHistoryLinkCandidateByTokenMutation();

  const [utm_source] = useQueryParams(['utm_source']);
  const [admin] = useQueryParams(['admin']);
  const [showIntroduction, setShowIntroduction] = useState(true);
  const [displayReferralCard, setDisplayReferralCard] = useState(false);
  const [showChecksList, setShowChecksList] = useState(false);
  const [showHelpModal, setShowHelpModal] = useState(false);
  const [helpModalContent, setHelpModalContent] = useState(null);
  const [helpModalCheckTypeTitle, setHelpModalCheckTypeTitle] = useState(null);
  const [alertCancelChecks, setAlertCancelChecks] = useState(false);
  // Helper to build navigation path with admin parameter preserved
  const buildNavPath = path => buildNavPathHelper(path, admin);

  // Save utm_source
  useEffect(() => {
    if (!utm_source || token == null) return;

    setHistoryLinkCandidate(token);
  }, [utm_source, token]);

  const onRedirect = () => {
    if (token == null) return;
    navigate(buildNavPath(`/form_submission/candidate/dashboard/${token}`));
  };

  const referralCandidateChecks = useMemo(() => {
    return (candidate_checks || []).filter(cc => cc?.details?.referral_candidate);
  }, [candidate_checks]);

  const introductionCheckRows = useMemo(() => {
    const submitted = [];
    const notSubmitted = [];

    (candidate_checks || [])
      .filter(cc => cc && !cc.details?.bypassed)
      .forEach(candidate_check => {
        if (!candidate_check) return;
        const check_type = (check_types || []).find(ct => ct?.slug === candidate_check?.type) || {};

        if (
          candidate_check?.type === 'candidate_questionnaire' &&
          candidate_check?.sub_checks?.length
        ) {
          (candidate_check.sub_checks || []).forEach(sub_check => {
            if (sub_check == null) return;
            const row = {
              key: `sub-${sub_check.id}`,
              slug: check_type?.slug,
              title: sub_check?.title,
            };
            if (sub_check.submitted_at) {
              submitted.push(row);
            } else {
              notSubmitted.push(row);
            }
          });
          return;
        }

        const row = {
          key: `check-${candidate_check.id}`,
          slug: check_type?.slug,
          title: check_type?.title,
        };
        if (candidate_check.submitted_at) {
          submitted.push(row);
        } else {
          notSubmitted.push(row);
        }
      });

    return { submitted, notSubmitted };
  }, [candidate_checks, check_types]);

  const {
    scrollAreaRef: checksScrollAreaRef,
    scrollContentRef: checksScrollContentRef,
    hasScroll: checksListHasScroll,
    atEnd: checksListAtEnd,
    isScrolling: checksListScrolling,
    onScroll: onChecksListScroll,
  } = useScrollAreaOverflow({
    active: showChecksList,
    observeDeps: [introductionCheckRows],
  });

  const getCheckTypeTitle = candidate_check => {
    if (candidate_check == null) return undefined;
    const check_type = (check_types || []).find(ct => ct?.slug === candidate_check);
    return check_type?.title;
  };

  const handleCancelChecks = async () => {
    if (token == null) return;
    try {
      await dispatch(
        cancelCandidateChecks({
          token,
          data: {
            check_ids: (referralCandidateChecks || [])
              .filter(check => check?.id != null)
              .map(check => check.id),
          },
        }),
      ).unwrap();
      setDisplayReferralCard(false);
      handleApplicationRoute();
    } catch (error) {
      console.error(error);
    }
  };

  const handleApplicationRoute = async () => {
    if (token == null) return;
    try {
      const result = await dispatch(getCandidateApplication(token)).unwrap();
      const checks = result?.candidate_checks || [];
      if (checks.length > 0) {
        setShowChecksList(true);
      } else {
        navigate(`/thanks?only_info=true`);
      }
    } catch (error) {
      console.error('Error getting candidate application', error);
      navigate(`/thanks?only_info=true`);
    }
  };

  const handleCheckDisplay = () => {
    setShowIntroduction(false);
    if (
      referralCandidateChecks.length > 0 &&
      referralCandidateChecks.some(check => check?.extra_data?.referral_accepted !== true)
    ) {
      setDisplayReferralCard(true);
    } else {
      setShowChecksList(true);
    }
  };

  const proceedToChecks = async () => {
    if (token == null) return;
    try {
      await Promise.all(
        (referralCandidateChecks || [])
          .filter(check => check?.id != null)
          .map(check =>
            dispatch(
              updateCandidateCheck({
                token,
                id: check.id,
                data: { extra_data: { referral_accepted: true } },
              }),
            ).unwrap(),
          ),
      );
      setDisplayReferralCard(false);
      setShowChecksList(true);
    } catch (error) {
      console.error('Failed to accept referral check(s):', error);
    }
  };

  const openHelpModal = check_type => {
    if (check_type == null) return;
    const getContent = CandidateHelpModalContent[check_type];
    if (typeof getContent !== 'function') return;
    setShowHelpModal(true);
    setHelpModalContent(getContent(t, { companyName: brand?.brand_name || company_name }));
    setHelpModalCheckTypeTitle(getCheckTypeTitle(check_type));
  };

  const brandStyle = {
    '--brand-button-bg': brand?.color_button,
    '--brand-button-text': brand?.color_button_text,
    '--brand-link-color': brand?.color_link,
    '--brand-bg-light': `${brand?.color_button ? brand.color_button + '40' : 'background-gradient'}`,
  };

  const brandbackgroundLight = brand?.color_button
    ? 'bg-[var(--brand-bg-light)]'
    : 'bg-background-gradient';
  const linkClass = brand?.color_link ? 'text-[var(--brand-link-color)]' : 'text-primary-4';

  const introductionModalHtml = useMemo(() => {
    const variants = t('candidate.introduction.modalHtml', { returnObjects: true });
    const raw = variants?.[candidate?.volunteer ? 'volunteer' : 'employment'] ?? '';
    return DOMPurify.sanitize(raw, {
      ADD_ATTR: ['target', 'rel', 'href', 'class'],
      ALLOWED_TAGS: ['br', 'a'],
    });
  }, [t, candidate?.volunteer]);

  const introductionModalLinkStyle =
    brand?.brand_active && brand?.color_active && brand?.color_link
      ? { '--candidate-intro-link-color': brand.color_link }
      : undefined;

  return (
    <CandidatePage token={token} withTopLogo brand={brand}>
      <AnimatedModal visible={showIntroduction} small noClose title="Introduction to Checkmate">
        <p
          className={cn('u-margin-top--large', styles.introductionModalCopy)}
          style={introductionModalLinkStyle}
          dangerouslySetInnerHTML={{ __html: introductionModalHtml }}
        />
        <CustomButton
          brand={brand}
          small
          onClick={() => handleCheckDisplay()}
          className="u-margin-top--large">
          Continue
        </CustomButton>
      </AnimatedModal>

      <Alert
        show={alertCancelChecks}
        title="Do you want to decline this background check referral?"
        message={`If you decline, ${referralCandidateChecks.length > 1 ? 'these background checks' : 'this background check'}  will not be processed. This may impact your onboarding, and your employer may ask you to complete it again later.`}
        ok="Decline"
        onOk={() => {
          setAlertCancelChecks(false);
          handleCancelChecks();
        }}
        cancel="Go Back"
        icon={Cancel01Icon}
        showConfirmationInput
        type="error"
        onCancel={() => setAlertCancelChecks(false)}
      />

      <AnimatedModal
        visible={showHelpModal}
        small
        icon={HelpCircleIcon}
        oldIconLibrary={false}
        title="Check process"
        description={helpModalCheckTypeTitle}
        onClose={() => setShowHelpModal(false)}>
        <div className="flex flex-col gap-4 pt-4">
          {helpModalContent?.description?.content?.map((content, index) => (
            <div key={index} className="flex items-start gap-4">
              <Badge text={index + 1} variant="default" />
              <p className="text-text-secondary text-lg">{content}</p>
            </div>
          ))}
        </div>
      </AnimatedModal>

      {referralCandidateChecks.length > 0 && displayReferralCard && (
        <div className="w-full max-w-3xl mx-auto animate-fade-in">
          {/* Main Card */}
          <div className="bg-card rounded-2xl shadow-card border border-border overflow-hidden shadow-sm">
            {/* Header Section */}
            <div
              className={`flex flex-col items-center justify-center gap-4 p-12 text-center ${brandbackgroundLight}`}
              style={brandStyle}>
              <IconComponent icon={HonourStarIcon} size="xlarge" strokeWidth={1.5} />
              {/* <img src="/assets/images/checkmate/icon-label.svg" alt="Checkmate logo" className="w-40 mx-auto mb-4" /> */}
              <div className="flex flex-col items-center justify-center gap-2">
                <h1 className="text-4xl font-semibold text-text-primary">
                  Background check referral
                </h1>
                <div className="flex items-center gap-4">
                  <div className="flex flex-col items-start justify-center flex-1">
                    <p className="text-text-primary text-2xl">
                      <span className="font-regular">by</span>{' '}
                      <span className="font-medium">{brand?.brand_name || company_name}</span>
                    </p>
                  </div>
                </div>
              </div>
            </div>

            {/* Content Section */}
            <div className="p-8 space-y-6 bg-surface-secondary/50 ">
              <p className="text-text-secondary text-lg">
                {brand?.brand_name || company_name} has referred you to use Checkmate Technology to
                complete an AU Nationally Coordinated Criminal History Check. Checkmate Technology
                will collect some details about you and work with the Australian Criminal
                Intelligence Commission as well as state and federal police services to complete the
                check.
              </p>
              <p className="text-text-secondary text-lg">
                To accept the referral and proceed with the check, please click the 'Request Check'
                button below.
              </p>
              {/* Check Type */}
              <div className="flex flex-col items-center rounded-xl bg-surface-primary rounded-3xl border border-border-primary">
                <div className="py-4 px-8 flex flex-col gap-4">
                  {(referralCandidateChecks || [])?.map(check => (
                    <div key={check?.type} className="flex items-center gap-4">
                      <IconComponent
                        icon={getCheckTypeIcon(check?.type)}
                        size="large"
                        strokeWidth={1.5}
                      />
                      <div className="flex-1">
                        <p className="font-medium text-text-primary text-2xl">
                          {getCheckTypeTitle(check?.type)}
                        </p>
                      </div>
                      {CandidateHelpModalContent[check?.type] && (
                        <HugeiconsIcon
                          size="20"
                          icon={HelpCircleIcon}
                          strokeWidth="1.5"
                          className="text-text-tertiary cursor-pointer"
                          onClick={() => openHelpModal(check?.type)}
                        />
                      )}
                    </div>
                  ))}
                </div>
              </div>
            </div>

            <hr className="border-border-primary" />

            <div className="p-8 flex flex-col gap-2">
              <div className="flex items-start gap-4">
                <div className="shrink-0 mt-0.5">
                  <HugeiconsIcon
                    size="20"
                    icon={ShieldKeyIcon}
                    strokeWidth="1.5"
                    className="text-text-tertiary"
                  />
                </div>
                <span className="text-lg text-text-tertiary">
                  Once completed, Checkmate Technology will provide you with a detailed report via a
                  secure candidate portal link. Once you have reviewed the report, you may choose to
                  share the results with {brand?.brand_name || company_name}. You are under no
                  obligation to share the check results, and Checkmate Technology will not share the
                  results unless you explicitly approve us to do so.
                </span>
              </div>
              <div className="flex items-start gap-4">
                <div className="shrink-0 mt-0.5">
                  <HugeiconsIcon
                    size="20"
                    icon={UserTime02Icon}
                    strokeWidth="1.5"
                    className="text-text-tertiary"
                  />
                </div>
                <span className="text-lg text-text-tertiary">
                  You control who sees your results. You may download a copy of the check result for
                  your own records, or to share with other potential employers, however Checkmate
                  Technology will automatically delete the check result 90 days after it was
                  completed.
                </span>
              </div>
            </div>

            <div className="px-8 pb-8 flex flex-col sm:flex-row gap-3">
              <CustomButton
                small
                type="secondary"
                className="sm:!w-1/3 w-full"
                onClick={() => setAlertCancelChecks(true)}>
                No Thanks
              </CustomButton>
              <CustomButton small brand={brand} onClick={() => proceedToChecks()}>
                {referralCandidateChecks.length > 1 ? 'Request Checks' : 'Request Check'}
              </CustomButton>
            </div>

            <div className="pb-8 text-center">
              <p className="text-sm text-text-tertiary">
                By requesting, you agree to our{' '}
                <a
                  href="https://www.checkmate.tech/utility/terms-and-conditions"
                  target="_blank"
                  className={`font-medium ${linkClass}`}>
                  Terms and Conditions
                </a>{' '}
                and{' '}
                <a
                  href="https://www.checkmate.tech/utility/privacy-policy"
                  target="_blank"
                  className={`font-medium ${linkClass}`}>
                  Privacy Policy
                </a>
              </p>
            </div>
          </div>
        </div>
      )}

      {showChecksList && (
        <CandidatePage.View>
          <div className="flex flex-col gap-8 sm:w-3/4 w-full">
            <div className="flex flex-col gap-4">
              <div className="flex flex-col gap-2">
                <span className="text-text-tertiary text-xl">Welcome to Checkmate!</span>
                <h1 className="text-5xl font-semibold">{candidate?.first_name} 👋</h1>
              </div>
              <p className="text-text-secondary text-2xl font-light">
                {candidate?.volunteer
                  ? brand?.volunteer_introduction_text ||
                    'This system is a secure verification platform designed to simplify the process for organisations and volunteers.'
                  : brand?.candidate_introduction_text ||
                    'This system is a secure verification platform designed to simplify the process for employers and employees.'}
              </p>
            </div>
            <AnimatePresence mode="wait">
              <SliderMotion key={candidate?.first_name} duration={0.6} isBackward={false}>
                <div className="flex flex-col gap-4 border border-neutral-4 rounded-3xl bg-neutral-2/50">
                  <div
                    ref={checksScrollAreaRef}
                    onScroll={onChecksListScroll}
                    className="flex max-h-[400px] flex-col gap-4 overflow-y-auto relative">
                    <div ref={checksScrollContentRef} className="flex flex-col gap-4">
                      <IntroductionCheckListSection
                        title="To do"
                        rows={introductionCheckRows?.notSubmitted || []}
                        variant="action"
                      />
                      <IntroductionCheckListSection
                        title="Completed"
                        rows={introductionCheckRows?.submitted || []}
                        variant="done"
                      />
                    </div>
                    <AnimatePresence>
                      {checksListHasScroll && !checksListAtEnd && (
                        <motion.span
                          key="checks-scroll-hint"
                          className="pointer-events-none absolute bottom-5 left-1/2 z-10 flex w-max max-w-full -translate-x-1/2 items-center justify-center gap-1 whitespace-nowrap rounded-full bg-neutral-1 p-2 text-text-tertiary text-xl shadow-2xl border border-neutral-4"
                          initial={{ opacity: 0 }}
                          animate={{ opacity: checksListScrolling ? 0 : 1 }}
                          exit={{ opacity: 0 }}
                          transition={{ duration: 0.35, ease: 'easeInOut' }}>
                          <HugeiconsIcon icon={ArrowDown02Icon} size="16" strokeWidth="1.5" />
                          Scroll to view more
                        </motion.span>
                      )}
                    </AnimatePresence>
                  </div>
                </div>

                <div className="flex mt-8">
                  <CustomButton brand={brand} className="u-margin-left--small" onClick={onRedirect}>
                    Get Started
                  </CustomButton>
                </div>
              </SliderMotion>
            </AnimatePresence>
          </div>
        </CandidatePage.View>
      )}
    </CandidatePage>
  );
};

export default CandidateIntroduction;
